/**
 * The external dependencies
 */

/** @jsxRuntime classic */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import { observer } from 'mobx-react-lite';
import { Box, Grid } from '@mui/material';

/**
 * The internal dependencies
 */
import {
  CreatorRole,
  GetChangeDto,
  ProcessListForChangeDto,
  UpdateChangeDtoStatusEnum,
} from '@codefluegel/zeta-change-typescript-client';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TodoCard from '../../components/TodoCard/TodoCard';
import MaterialTable from '../../components/Material/MaterialTable';
import { ReducedMaterialsType } from '../../types/materials';
import { ReducedServicesType } from '../../types/services';
import ServiceModal from '../../components/Dialog/ServiceModal';
import { useChangeStore } from '../../store/changeStore';
import MaterialModal from '../../components/Dialog/MaterialModal';
import { ReducedWorkPackagesType } from '../../types/workPackages';
import { useStorex } from '../../store/UIStore';
import { getDepartmentById } from '../../utils/utils';

export type CalculationHoursPageProps = {
  isTabDataVisible: boolean;
  processes: ProcessListForChangeDto[];
  workMaterials: ReducedMaterialsType;
  services: ReducedServicesType;
  isLoading: boolean;
  workPackages: ReducedWorkPackagesType;
  handleChangeProcess: (fieldName: string, value: number, id: number) => void;
  change: GetChangeDto | null;
  editable: boolean;
};

const CalculationServiceMaterialTab = observer(
  ({
    isTabDataVisible,
    processes,
    workMaterials,
    workPackages,
    services,
    isLoading,
    handleChangeProcess,
    change,
    editable,
  }: CalculationHoursPageProps) => {
    const [activeProcess, setActiveProcess] = useState<number | null>(null);
    const [modalProcess, setModalProcess] =
      useState<ProcessListForChangeDto | null>(null);
    const {
      pushNewMaterialPackage,
      pushNewServicePackage,
      reducedServices,
      reducedWorkMaterials,
      removeServicePackage,
      replaceUpdatedServicePackage,
      removeMaterialPackage,
      replaceUpdatedMaterialPackage,
    } = useChangeStore();

    const { t } = useTranslation();
    const [isServiceModalOpen, setServiceModalOpen] = useState(false);
    const [isMaterialModalOpen, setMaterialModalOpen] = useState(false);
    const [selectedServiceId, setSelectedServiceId] = useState<number | null>(
      null,
    );
    const [selectedMaterialId, setSelectedMaterialId] = useState<number | null>(
      null,
    );
    const { setNotification } = useStorex();

    const handleActiveProcess = useCallback(
      (id: number) => {
        setActiveProcess(id);
      },
      [setActiveProcess],
    );

    const creatorRole =
      change?.status === UpdateChangeDtoStatusEnum.EvalAccepted
        ? CreatorRole.CalculationResponsible
        : CreatorRole.ChangeManager;

    const handleServiceModal = useCallback(
      (workServiceId?: number, process?: ProcessListForChangeDto) => {
        setServiceModalOpen(!isServiceModalOpen);
        setSelectedServiceId(workServiceId ?? null);
        setModalProcess(process ?? null);
      },
      [isServiceModalOpen],
    );

    const handleMaterialModal = useCallback(
      (workMaterialId?: number, process?: ProcessListForChangeDto) => {
        setMaterialModalOpen(!isMaterialModalOpen);
        setSelectedMaterialId(workMaterialId ?? null);
        setModalProcess(process ?? null);
      },
      [isMaterialModalOpen],
    );

    return (
      <Grid
        container
        size={12}
        direction="column"
        css={css`
          display: ${isTabDataVisible ? 'flex' : 'none'};
          padding: 19px 0px 12px 0px;
        `}
      >
        <TodoCard text={t('general.infoText.PleaseAddMaterial')} />

        <Grid
          container
          size={12}
          css={css`
            margin-top: 20px;
            gap: 20px;
          `}
        >
          {processes
            .slice()
            .sort((a, b) => {
              const aDepartment = getDepartmentById(
                a.pId,
                change?.commonProject?.navProcessToCommonProjectRelations,
              );
              const aGroupId = aDepartment?.groupNo ?? 0;

              const bDepartment = getDepartmentById(
                b.pId,
                change?.commonProject?.navProcessToCommonProjectRelations,
              );
              const bGroupId = bDepartment?.groupNo ?? 0;
              return aGroupId - bGroupId;
            })
            .map(process => (
              <Box sx={{ width: '98%' }} key={process.id}>
                <MaterialTable
                  change={change}
                  workPackages={workPackages[process.id]}
                  process={process}
                  workMaterials={workMaterials[process.id] || []}
                  services={services[process.id] || []}
                  handleChangeProcess={handleChangeProcess}
                  handleActiveProcess={handleActiveProcess}
                  editable={editable}
                  isActive={
                    activeProcess ? activeProcess === process.id : false
                  }
                  isLoading={isLoading}
                  handleServiceModal={(id: number | undefined) =>
                    handleServiceModal(id, process)
                  }
                  handleMaterialModal={(id: number | undefined) =>
                    handleMaterialModal(id, process)
                  }
                />
              </Box>
            ))}
        </Grid>
        {/* Render modals only once, outside the map, for the selected process */}
        {change && modalProcess && (
          <Box>
            <ServiceModal
              isOpen={isServiceModalOpen}
              handleModal={() => {
                setServiceModalOpen(false);
                setSelectedServiceId(null);
                setModalProcess(null);
              }}
              pushNewServicePackage={pushNewServicePackage}
              replaceUpdatedServicePackage={replaceUpdatedServicePackage}
              removeServicePackage={removeServicePackage}
              change={change}
              setNotification={setNotification}
              creatorRole={creatorRole}
              currentProcess={modalProcess}
              selectedService={
                selectedServiceId != null
                  ? reducedServices[modalProcess.id]?.find(
                      s => s.id === selectedServiceId,
                    )
                  : undefined
              }
            />
            <MaterialModal
              isOpen={isMaterialModalOpen}
              handleModal={() => {
                setMaterialModalOpen(false);
                setSelectedMaterialId(null);
                setModalProcess(null);
              }}
              creatorRole={creatorRole}
              setNotification={setNotification}
              pushNewMaterialPackage={pushNewMaterialPackage}
              replaceUpdatedMaterialPackage={replaceUpdatedMaterialPackage}
              removeMaterialPackage={removeMaterialPackage}
              change={change}
              currentProcess={modalProcess}
              selectedMaterial={
                selectedMaterialId != null
                  ? reducedWorkMaterials[modalProcess.id]?.find(
                      m => m.id === selectedMaterialId,
                    )
                  : undefined
              }
            />
          </Box>
        )}
      </Grid>
    );
  },
);

export default CalculationServiceMaterialTab;
